import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "@/lib/toast";
import ProjectChatPanel from "@/components/ProjectChatPanel";
import { MessageSquare, ChevronRight, Search } from "lucide-react";

const STATUS_TONE = {
  ordered: "bg-slate-200 text-slate-700",
  in_process: "bg-blue-100 text-blue-700",
  awaiting_arrival: "bg-amber-100 text-amber-700",
  scheduling: "bg-purple-100 text-purple-700",
  scheduled: "bg-indigo-100 text-indigo-700",
  installed: "bg-emerald-100 text-emerald-700",
  completed: "bg-emerald-100 text-emerald-700",
  cancelled: "bg-red-100 text-red-700",
};

export default function AdminProjectMessages() {
  const [rooms, setRooms] = useState([]);
  const [active, setActive] = useState(null);
  const [q, setQ] = useState("");
  const [params, setParams] = useSearchParams();
  const preselect = params.get("project");

  const load = async () => {
    try {
      const r = await api.get("/admin/project-message-rooms");
      setRooms(r.data);
      if (preselect) {
        const found = r.data.find((x) => x.project_id === preselect);
        if (found) setActive(found);
      } else if (!active && r.data.length > 0) {
        setActive(r.data[0]);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed");
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const onPick = (r) => {
    setActive(r);
    setParams({ project: r.project_id }, { replace: true });
    // Optimistically zero out unread.
    setRooms((prev) => prev.map((x) => x.project_id === r.project_id ? { ...x, unread_count: 0 } : x));
  };

  const filtered = rooms.filter((r) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (
      (r.client_name || "").toLowerCase().includes(s) ||
      (r.customer_code || "").toLowerCase().includes(s) ||
      (r.project_title || "").toLowerCase().includes(s) ||
      (r.project_number || "").toLowerCase().includes(s)
    );
  });

  const totalUnread = rooms.reduce((sum, r) => sum + (r.unread_count || 0), 0);

  return (
    <div data-testid="admin-project-messages" className="space-y-6">
      <div>
        <p className="overline">Client conversations</p>
        <h1 className="text-3xl font-heading font-medium text-[#0A2540] dark:text-white mt-2">Project Messages</h1>
        <p className="text-sm text-[#475569] dark:text-slate-400 mt-1">
          One thread per project · {rooms.length} active
          {totalUnread > 0 && (
            <span className="ml-2 inline-block px-2 py-0.5 bg-red-500 text-white text-xs font-bold rounded-full">
              {totalUnread} unread
            </span>
          )}
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Room list */}
        <div className="card-pb overflow-hidden max-h-[75vh] flex flex-col">
          <div className="px-4 py-3 border-b border-[#E2E8F0] dark:border-slate-700 flex items-center gap-2 bg-slate-50/60 dark:bg-slate-900/40">
            <Search className="w-4 h-4 text-[#94A3B8]" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by client, project, code…"
              data-testid="pmessages-search"
              className="flex-1 bg-transparent text-sm outline-none text-[#0A2540] dark:text-white placeholder:text-[#94A3B8]"
            />
          </div>
          <ul className="flex-1 overflow-y-auto">
            {filtered.length === 0 && (
              <li className="px-5 py-8 text-center text-sm text-[#94A3B8]">
                {rooms.length === 0 ? "No client messages yet." : "No matches for your search."}
              </li>
            )}
            {filtered.map((r) => {
              const isActive = active?.project_id === r.project_id;
              const last = r.last_message;
              return (
                <li key={r.project_id}>
                  <button
                    onClick={() => onPick(r)}
                    data-testid={`pmessages-room-${r.project_id}`}
                    className={`w-full text-left px-4 py-3 border-b border-[#E2E8F0] dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 transition ${isActive ? "bg-[#FFFBF0] dark:bg-slate-800" : ""}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm text-[#0A2540] dark:text-white truncate">
                            {r.client_name || "—"}
                          </span>
                          {r.unread_count > 0 && (
                            <span
                              className="min-w-[18px] h-[18px] px-1.5 grid place-items-center rounded-full bg-red-500 text-white text-[10px] font-bold"
                              data-testid={`pmessages-unread-${r.project_id}`}
                            >
                              {r.unread_count}
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-[#475569] dark:text-slate-400 truncate">
                          {r.project_number} · {r.customer_code || ""}
                        </div>
                        <div className="text-xs text-[#0A2540] dark:text-slate-300 truncate mt-1">
                          {r.project_title}
                        </div>
                        {last && (
                          <div className="text-[11px] text-[#475569] dark:text-slate-400 truncate mt-1">
                            <span className="font-medium">
                              {last.sender_role === "client" ? "Client" : "You"}:
                            </span>{" "}
                            {last.text || (last.has_attachments ? "📎 Attachment" : "")}
                          </div>
                        )}
                      </div>
                      <div className="text-right shrink-0">
                        {r.project_status && (
                          <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] uppercase font-bold ${STATUS_TONE[r.project_status] || "bg-slate-200 text-slate-700"}`}>
                            {(r.project_status || "").replace(/_/g, " ")}
                          </span>
                        )}
                        {last?.created_at && (
                          <div className="text-[10px] text-[#94A3B8] mt-1">
                            {new Date(last.created_at).toLocaleDateString([], { month: "short", day: "numeric" })}
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Chat pane */}
        <div className="lg:col-span-2">
          {active ? (
            <div className="space-y-3">
              <div className="card-pb px-5 py-3 flex items-center justify-between">
                <div>
                  <div className="text-xs text-[#94A3B8]">{active.project_number} · {active.customer_code}</div>
                  <div className="font-heading text-lg text-[#0A2540] dark:text-white">{active.project_title}</div>
                  <div className="text-xs text-[#475569] dark:text-slate-400">Client: {active.client_name}</div>
                </div>
                <Link
                  to={`/admin/projects/${active.project_id}`}
                  className="text-xs text-[#C9A356] hover:underline inline-flex items-center gap-0.5"
                  data-testid="open-project-detail"
                >
                  Open project <ChevronRight className="w-3 h-3" />
                </Link>
              </div>
              <ProjectChatPanel
                projectId={active.project_id}
                title={`${active.client_name || ""} · ${active.customer_code || ""}`}
                subtitle={active.project_title}
                height="h-[64vh]"
              />
            </div>
          ) : (
            <div className="card-pb p-12 text-center text-[#475569] dark:text-slate-400">
              <MessageSquare className="w-10 h-10 mx-auto text-[#94A3B8] mb-2" />
              <p>Select a project to view the conversation</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
